// @ts-check
// IR-JSON -> pseudocódigo PSeInt (DEC-107).
//
// El resultado tiene que ser pseudocódigo EJECUTABLE por el intérprete que ya
// vive en `pseudocode-editor/pseint-interpreter.js`, no una aproximación
// legible. Esa es la diferencia con `utils/ir-legible.js`, que formatea para
// MOSTRAR y por eso puede degradar a JSON cuando encuentra algo raro: aquí
// degradar sería entregar al alumno un programa que no compila.
//
// ## Correspondencias que no son obvias
//
// - **`RepetirHasta`** existe en PSeInt tal cual (`Repetir ... Hasta Que`), así
//   que sale directo. Es en Python donde hay que rodearlo.
// - **`BucleFinPara` con paso negativo**: PSeInt admite `Con Paso -1`, así que
//   no hay caso especial.
// - **`Llamada` con `variable_destino`** en posición de sentencia se emite como
//   una asignación (`x <- f(...)`), que es como se escribe en PSeInt.
// - **`Retorno`** NO se traduce a una instrucción de retorno, porque PSeInt no
//   tiene ninguna: el valor devuelto es el de la variable de la cabecera. Un
//   retorno anticipado (que no sea la última instrucción) se RECHAZA, porque
//   no hay forma de expresar "sal ya" y fingirlo cambiaría el resultado.
// - **Los tipos** (`Definir ... Como Entero`) se emiten solo cuando la IR los
//   trae. Inventar un tipo sería adivinar, y una declaración con el tipo
//   equivocado rompe la ejecución más adelante, lejos de donde se inventó.

import { crearRegistroDeMotivos, traduccionFallida, traduccionOk, unirLineas } from './resultado.js';

/**
 * Sangría de un nivel: UN TABULADOR.
 *
 * No es una preferencia de estilo. El editor de pseudocódigo comprueba el
 * anidamiento contando tabuladores al principio de cada línea y se detiene en
 * el primer carácter que no lo sea (`pseudocode-editor/pseudo-indentation.js`).
 * Con espacios, el análisis ve nivel 0 en todas las líneas y marca el programa
 * entero con avisos rojos de indentación -- código correcto que se le presenta
 * al alumno como si estuviera mal.
 */
const SANGRIA = '\t';

const TIPO_PSEINT = /** @type {Record<string, string>} */ ({
  entero: 'Entero',
  decimal: 'Real',
  cadena: 'Caracter',
  booleano: 'Logico',
});

/** Operadores que PSeInt escribe distinto que la IR. */
const OPERADOR_PSEINT = /** @type {Record<string, string>} */ ({
  '==': '=',
  '!=': '<>',
  Y: 'Y',
  O: 'O',
  NO: 'NO',
});

/**
 * @param {unknown} valor
 * @returns {valor is Record<string, unknown>}
 */
function esNodo(valor) {
  return typeof valor === 'object' && valor !== null && typeof (/** @type {any} */ (valor).nodo) === 'string';
}

/**
 * Traduce un nodo de TIPO a su nombre en PSeInt.
 * @param {unknown} tipo
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @returns {string|null}
 */
function tipoAPseint(tipo, registro) {
  if (typeof tipo === 'string') {
    const nombre = TIPO_PSEINT[tipo];
    if (nombre) return nombre;
    registro.anotar(`El tipo "${tipo}" no existe en pseudocódigo.`);
    return null;
  }
  if (esNodo(tipo) && tipo.nodo === 'Lista') {
    // PSeInt declara los arreglos con `Dimension`, que necesita un TAMAÑO que
    // la IR no guarda en el tipo. Traducirlo exigiría inventárselo.
    registro.anotar(
      'Las listas no se pueden declarar automáticamente en pseudocódigo: falta el tamaño.',
    );
    return null;
  }
  return null;
}

/**
 * Traduce una expresión de la IR a texto PSeInt.
 * @param {unknown} expresionValor
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {string} donde
 * @returns {string}
 */
function expresion(expresionValor, registro, donde) {
  if (!esNodo(expresionValor)) {
    registro.anotar('Hay una expresión vacía o mal formada.', donde);
    return '0';
  }
  switch (expresionValor.nodo) {
    case 'Literal': {
      const valor = expresionValor.valor;
      if (typeof valor === 'string') return `"${valor.replace(/"/g, '""')}"`;
      if (typeof valor === 'boolean') return valor ? 'Verdadero' : 'Falso';
      return String(valor);
    }
    case 'ReferenciaVariable':
      return String(expresionValor.nombre ?? '');
    case 'Operacion': {
      const operandos = Array.isArray(expresionValor.operandos) ? expresionValor.operandos : [];
      const bruto = String(expresionValor.operador ?? '');
      const op = OPERADOR_PSEINT[bruto] ?? bruto;
      if (bruto === 'NEG') return `-(${expresion(operandos[0], registro, donde)})`;
      if (bruto === 'NO') return `NO (${expresion(operandos[0], registro, donde)})`;
      // Se parentiza SIEMPRE. Un traductor no debería depender de acertar con
      // la precedencia de dos lenguajes a la vez: sobran paréntesis, pero el
      // programa significa exactamente lo mismo que el original.
      return `(${expresion(operandos[0], registro, donde)} ${op} ${expresion(operandos[1], registro, donde)})`;
    }
    case 'Llamada': {
      const argumentos = Array.isArray(expresionValor.argumentos) ? expresionValor.argumentos : [];
      const args = argumentos.map((a) => expresion(a, registro, donde)).join(', ');
      return `${String(expresionValor.nombre ?? '')}(${args})`;
    }
    case 'AccesoIndice':
      return `${expresion(expresionValor.lista, registro, donde)}[${expresion(expresionValor.indice, registro, donde)}]`;
    default:
      registro.anotar(`No sé escribir "${String(expresionValor.nodo)}" en pseudocódigo.`, donde);
      return '0';
  }
}

/**
 * Traduce una lista de sentencias.
 * @param {unknown} secuencia
 * @param {number} nivel
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {string} donde
 * @returns {string[]}
 */
function sentencias(secuencia, nivel, registro, donde) {
  const lista =
    esNodo(secuencia) && Array.isArray(secuencia.sentencias) ? secuencia.sentencias : [];
  /** @type {string[]} */
  const salida = [];
  for (const sentencia of lista) {
    salida.push(...unaSentencia(sentencia, nivel, registro, donde));
  }
  return salida;
}

/**
 * @param {unknown} sentencia
 * @param {number} nivel
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {string} donde
 * @returns {string[]}
 */
function unaSentencia(sentencia, nivel, registro, donde) {
  const s = SANGRIA.repeat(nivel);
  if (!esNodo(sentencia)) {
    registro.anotar('Hay un bloque vacío o mal formado.', donde);
    return [];
  }
  switch (sentencia.nodo) {
    case 'Declaracion': {
      const tipo = tipoAPseint(sentencia.tipo, registro);
      if (tipo === null) return [];
      return [`${s}Definir ${String(sentencia.nombre ?? '')} Como ${tipo}`];
    }
    case 'Asignacion':
      return [
        `${s}${String(sentencia.variable ?? '')} <- ${expresion(sentencia.expresion, registro, donde)}`,
      ];
    case 'Entrada':
      return [`${s}Leer ${String(sentencia.variable ?? '')}`];
    case 'Salida':
      return [`${s}Escribir ${expresion(sentencia.expresion, registro, donde)}`];
    case 'Comentario':
      // Un comentario de varias líneas necesita `//` en cada una, o la segunda
      // se interpretaría como instrucción.
      return String(sentencia.texto ?? '')
        .split('\n')
        .map((linea) => `${s}// ${linea}`);
    case 'Condicional': {
      const lineas = [
        `${s}Si ${expresion(sentencia.condicion, registro, donde)} Entonces`,
        ...sentencias(sentencia.rama_si, nivel + 1, registro, donde),
      ];
      const sino = sentencia.rama_sino;
      const tieneSino =
        esNodo(sino) && Array.isArray(sino.sentencias) && sino.sentencias.length > 0;
      if (tieneSino) {
        lineas.push(`${s}Sino`, ...sentencias(sino, nivel + 1, registro, donde));
      }
      lineas.push(`${s}FinSi`);
      return lineas;
    }
    case 'BucleMientras':
      return [
        `${s}Mientras ${expresion(sentencia.condicion, registro, donde)} Hacer`,
        ...sentencias(sentencia.cuerpo, nivel + 1, registro, donde),
        `${s}FinMientras`,
      ];
    case 'BucleFinPara': {
      const paso = expresion(sentencia.paso, registro, donde);
      // `Con Paso 1` es el valor por defecto de PSeInt: omitirlo cuando es 1
      // produce el pseudocódigo que un profesor escribiría a mano.
      const conPaso = paso === '1' ? '' : ` Con Paso ${paso}`;
      return [
        `${s}Para ${String(sentencia.variable ?? '')} <- ${expresion(sentencia.inicio, registro, donde)} Hasta ${expresion(sentencia.fin, registro, donde)}${conPaso} Hacer`,
        ...sentencias(sentencia.cuerpo, nivel + 1, registro, donde),
        `${s}FinPara`,
      ];
    }
    case 'RepetirHasta':
      return [
        `${s}Repetir`,
        ...sentencias(sentencia.cuerpo, nivel + 1, registro, donde),
        `${s}Hasta Que ${expresion(sentencia.condicion, registro, donde)}`,
      ];
    case 'Llamada': {
      const argumentos = Array.isArray(sentencia.argumentos) ? sentencia.argumentos : [];
      const args = argumentos.map((a) => expresion(a, registro, donde)).join(', ');
      const llamada = `${String(sentencia.nombre ?? '')}(${args})`;
      const destino = sentencia.variable_destino;
      return [typeof destino === 'string' && destino !== '' ? `${s}${destino} <- ${llamada}` : `${s}${llamada}`];
    }
    case 'Retorno': {
      // PSeInt NO tiene instrucción de retorno: el valor devuelto es el que
      // tenga al final la variable de la cabecera del subproceso. Por eso un
      // `Retorno` se escribe como una asignación a esa variable.
      //
      // Quien llame aquí (`unaFuncion`) ya ha comprobado que el retorno es la
      // ÚLTIMA sentencia del cuerpo; un retorno anticipado no se puede
      // expresar y se rechaza allí, no aquí.
      if (sentencia.expresion === null || sentencia.expresion === undefined) return [];
      return [`${s}resultado <- ${expresion(sentencia.expresion, registro, donde)}`];
    }
    default:
      registro.anotar(`No sé escribir el bloque "${String(sentencia.nodo)}" en pseudocódigo.`, donde);
      return [];
  }
}

/**
 * @param {Record<string, unknown>} funcion
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @returns {string[]}
 */
function unaFuncion(funcion, registro) {
  const nombre = String(funcion.nombre ?? '');
  const donde = `subproceso ${nombre}`;
  const parametros = Array.isArray(funcion.parametros) ? funcion.parametros : [];
  const nombresParametros = parametros
    .map((p) => (esNodo(p) ? String(p.nombre ?? '') : ''))
    .filter((n) => n !== '');

  // PSeInt distingue función (devuelve valor) de procedimiento por la forma de
  // la cabecera: `SubProceso resultado <- f(a, b)` frente a `SubProceso f(a)`.
  const devuelve = funcion.tipo_retorno !== null && funcion.tipo_retorno !== undefined;
  const cabecera = devuelve
    ? `SubProceso resultado <- ${nombre}(${nombresParametros.join(', ')})`
    : `SubProceso ${nombre}(${nombresParametros.join(', ')})`;

  // Un retorno que NO sea la última instrucción no se puede expresar en
  // pseudocódigo: PSeInt no puede "salir antes" de un subproceso. Traducirlo
  // como una asignación más produciría un programa que sigue ejecutando lo
  // que venía después, que es un resultado distinto y silencioso.
  const sentenciasCuerpo =
    esNodo(funcion.cuerpo) && Array.isArray(funcion.cuerpo.sentencias)
      ? funcion.cuerpo.sentencias
      : [];
  sentenciasCuerpo.forEach((sentencia, indice) => {
    const esRetorno = esNodo(sentencia) && sentencia.nodo === 'Retorno';
    if (esRetorno && indice !== sentenciasCuerpo.length - 1) {
      registro.anotar(
        'Hay un retorno en mitad de la función, y en pseudocódigo no se puede salir antes de tiempo.',
        donde,
      );
    }
  });

  const cuerpo = sentencias(funcion.cuerpo, 1, registro, donde);
  if (devuelve) {
    // La variable de la cabecera tiene que existir aunque el cuerpo no la
    // asigne por ningún camino.
    cuerpo.unshift(`${SANGRIA}resultado <- 0`);
  }
  return [cabecera, ...cuerpo, 'FinSubProceso', ''];
}

/**
 * Traduce un programa en IR-JSON a pseudocódigo PSeInt.
 *
 * @param {unknown} programa IR-JSON con forma `Programa`.
 * @returns {import('./resultado.js').Traduccion<string>}
 */
export function irAPseudocodigo(programa) {
  const registro = crearRegistroDeMotivos();
  if (!esNodo(programa) || programa.nodo !== 'Programa') {
    return traduccionFallida([{ descripcion: 'No hay ningún programa que traducir.' }]);
  }

  /** @type {string[]} */
  const lineas = [];

  const funciones = Array.isArray(programa.funciones) ? programa.funciones : [];
  for (const funcion of funciones) {
    if (!esNodo(funcion)) continue;
    lineas.push(...unaFuncion(funcion, registro));
  }

  lineas.push('Algoritmo principal');
  const declaraciones = Array.isArray(programa.declaraciones) ? programa.declaraciones : [];
  for (const declaracion of declaraciones) {
    lineas.push(...unaSentencia(declaracion, 1, registro, 'declaraciones'));
  }
  lineas.push(...sentencias(programa.cuerpo, 1, registro, 'programa principal'));
  lineas.push('FinAlgoritmo');

  if (registro.hayProblemas()) return traduccionFallida(registro.motivos());
  return traduccionOk(unirLineas(lineas));
}
